import type { Ref } from 'react'
import { Link } from '@tanstack/react-router'
import { Menu } from 'lucide-react'
import { normalizeRouteId, useCommittedRouteId } from '@region13/ui'
import { PHONE_TAP } from './ui'
import { COACH_NAV, PHONE_TABS } from '../lib/nav'

// Mobile bottom tab bar: the PHONE_TABS pages, labeled from COACH_NAV, then
// More, which opens the shared drawer listing all nine pages (Referees,
// Standings, Jerseys, Forms and Incidents are two taps away).
const TABS = PHONE_TABS.map((to) => COACH_NAV.items.find((i) => i.to === to)!)

// Dark brand-maroon chrome (the mobile analog of the schedule/admin sidebar):
// light labels, active tab brightened to full white — same active-is-brighter
// pattern the desktop sidebar uses. Inactive labels use white/75 (rather than
// the softer warm sidebar-ink token) to keep the small bold text comfortably
// above the AA contrast floor on the maroon bar.
const TAB_BASE = `flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-xs font-bold text-white/75 ${PHONE_TAP}`
export const TAB_ACTIVE_CLASS = '!text-white'
const LINK_ACTIVE = { className: TAB_ACTIVE_CLASS, 'aria-current': 'page' as const }
const TAB_PATHS: readonly string[] = PHONE_TABS

/** Fixed bottom nav, sized for a phone thumb. AppShell's content column pads
 *  its bottom so the last card is never hidden behind it. z-30 keeps it under
 *  the drawer's backdrop (z-40) while the drawer is open. */
export function BottomNav({
  moreOpen,
  onMore,
  moreRef,
}: {
  moreOpen: boolean
  onMore: () => void
  /** The More button, where focus returns when the drawer closes. */
  moreRef?: Ref<HTMLButtonElement>
}) {
  // On a page that is not a tab, More is how you got there: it takes the
  // active look, but not aria-current, since it is not that page.
  const routeId = useCommittedRouteId()
  const moreActive = routeId !== undefined && !TAB_PATHS.includes(normalizeRouteId(routeId))
  return (
    <nav aria-label="Primary" className="fixed bottom-0 inset-x-0 z-30 flex border-t border-sidebar-line bg-sidebar pb-[env(safe-area-inset-bottom)] md:hidden">
      {TABS.map(({ to, label, icon: Icon }) => (
        <Link key={to} to={to} activeOptions={{ exact: to === '/' }} activeProps={LINK_ACTIVE} className={TAB_BASE}>
          <Icon size={20} aria-hidden="true" />
          {label}
        </Link>
      ))}
      <button
        ref={moreRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        onClick={onMore}
        className={moreActive ? `${TAB_BASE} ${TAB_ACTIVE_CLASS}` : TAB_BASE}
      >
        <Menu size={20} aria-hidden="true" />
        More
      </button>
    </nav>
  )
}
